import type {
  Parameter,
  RawApiParameter,
  ParameterType
} from "@/types/parameter"

/**
 * Maps .NET CLR type names from the API to our internal ParameterType.
 * The upstream reporting service returns types like "System.Boolean",
 * while our React types use short names like "Bool".
 */
const TYPE_MAP: Record<string, ParameterType> = {
  "System.Boolean": "Bool",
  "System.String": "String",
  "System.Int64": "Int",
  "System.DateTime": "DateTime",
  "System.DateTimeOffset": "DateTime",
}

/**
 * Safely converts a value to string, guarding against objects that would
 * produce '[object Object]' stringification.
 */
function safeStringify(value: unknown): string {
  if (value == null) {
    return ""
  }

  const valueType = typeof value
  // Only stringify primitives; reject objects/arrays
  if (valueType === "object") {
    console.warn("Attempted to stringify object value:", value)
    return ""
  }

  // At this point, value is guaranteed to be a primitive (string, number, boolean, symbol, bigint)
  // Convert explicitly based on type to satisfy static analysis
  if (valueType === "string") {
    return value as string
  }
  if (valueType === "number") {
    return (value as number).toString()
  }
  if (valueType === "boolean") {
    return (value as boolean).toString()
  }
  if (valueType === "bigint") {
    return (value as bigint).toString()
  }
  // symbol or other edge cases - return empty string for safety
  console.warn("Unexpected value type in safeStringify:", valueType, value)
  return ""
}

/**
 * Converts a raw API value to an array of strings for MultiSelect parameters.
 * Handles single values, arrays, and null cases with auto-selection logic.
 */
function toStringArray(
  raw: RawApiParameter,
  base: { allowNull: boolean; availableValues: Array<{ value: unknown }> },
): string[] {
  if (Array.isArray(raw.value)) {
    return raw.value.map((v) => safeStringify(v))
  }

  if (raw.value != null) {
    return [safeStringify(raw.value)]
  }

  // Match C# MultiSelectParameter.StringValues setter behavior:
  // when value is null, allowNull is false, and availableValues exist,
  // auto-select ALL available values (see ParameterExtension.cs:240-243).
  if (!base.allowNull && base.availableValues.length > 0) {
    return base.availableValues.map((v) => safeStringify(v.value))
  }

  return []
}

const NO_DATA_TEXT = "No options available"

function hasNoDataAvailable(raw: RawApiParameter): boolean {
  const availableValues = raw.availableValues ?? []

  if (Array.isArray(raw.value)) {
    return raw.value.length === 0 && availableValues.length === 0
  }

  return raw.value == null && availableValues.length === 0
}

/**
 * Transforms a raw API parameter response into our typed Parameter union.
 *
 * The upstream API returns parameters with:
 * - `type`: CLR type names (e.g., "System.Boolean")
 * - `multivalue`: lowercase 'v' boolean flag
 * - `value`: a single field containing the parameter's value
 *
 * This function converts them to our discriminated union types with
 * specific value fields (boolValue, stringValue, etc.).
 */
export function transformParameter(raw: RawApiParameter): Parameter {
  const isMultiValue = raw.multivalue ?? raw.multiValue ?? false
  const children = Array.isArray(raw.children)
    ? raw.children.map(transformParameter)
    : []

  const base = {
    name: raw.name ?? "",
    text: hasNoDataAvailable(raw) ? NO_DATA_TEXT : raw.text ?? "",
    multiValue: isMultiValue,
    allowNull: raw.allowNull ?? true,
    isVisible: raw.isVisible ?? false,
    autoRefresh: raw.autoRefresh ?? false,
    hasChildParameters: raw.hasChildParameters ?? false,
    childParameters: raw.childParameters ?? [],
    availableValues: raw.availableValues ?? [],
    children,
    tenantId: raw.tenantId ?? 0,
  }

  // MultiSelect takes priority when multivalue is true
  if (isMultiValue) {
    return {
      ...base,
      type: "MultiSelect",
      multiValue: true,
      stringValues: toStringArray(raw, base),
    }
  }

  const mappedType = TYPE_MAP[raw.type]

  switch (mappedType) {
    case "Bool":
      // Note: boolValue is a JS boolean here. When serialized back to XML via
      // parametersToEntries() + getParameterStringValue(), Bool parameters are
      // converted to PascalCase strings ("True"/"False") that the C# backend expects.
      // The XMLParser is configured with parseTagValue: false to prevent round-trip
      // corruption of these string values.
      return {
        ...base,
        type: "Bool",
        boolValue: raw.value === true || raw.value === "true",
        isEnabled:
          raw.isEnabled ?? (raw.availableValues?.length === 2 || false),
      }

    case "String": {
      let stringValue = safeStringify(raw.value)
      // When value is empty/null, allowNull is false, and availableValues exist,
      // auto-select the first available value. This matches SSRS cascading behavior
      // where child parameters default to their first option after a parent changes.
      if (!stringValue && !base.allowNull && base.availableValues.length > 0) {
        stringValue = safeStringify(base.availableValues[0].value)
      }
      return {
        ...base,
        type: "String",
        stringValue,
      }
    }

    case "Int":
      return {
        ...base,
        type: "Int",
        intValue:
          typeof raw.value === "number"
            ? raw.value
            : Number.parseInt(safeStringify(raw.value) || "0", 10) || 0,
      }

    case "DateTime":
      return {
        ...base,
        type: "DateTime",
        dateTimeValue: safeStringify(raw.value),
      }

    default:
      // Fallback: treat unknown types as String parameters
      console.warn(
        `Unknown parameter type "${raw.type}" for parameter "${raw.name}", treating as String`,
      )
      return {
        ...base,
        type: "String",
        stringValue: safeStringify(raw.value),
      }
  }
}

/**
 * Transforms an array of raw API parameters into typed Parameters.
 */
export function transformParameters(raw: RawApiParameter[] | null | undefined): Parameter[] {
  // Guard against null, undefined, or non-array responses
  if (!raw || !Array.isArray(raw)) {
    console.warn("transformParameters received invalid input:", raw)
    return []
  }
  return raw.map(transformParameter)
}
